/*
  TODO: remove reachStockLimit() and import as util
*/

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { trackLineItemChange } from '../../utils/tracking';

import Button from '../../atoms/button';
import Select from '../../atoms/select';
import Typography from '../../atoms/typography';

import useObserver from '../../utils/hooks/useObserver';

import { useProduct } from '../../utils/context/product-provider';
import { useSearch } from '../../utils/context/search-provider';

const SubmitButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'displayOpacity' })`
  font-family: ${(props) => props.theme.font.sans};
  font-size: ${(props) => props.theme.modularScale.sixteen};  
  outline: 0;
  padding: 0 1rem;
  width: 100%;
  text-transform: none;
  letter-spacing: 0;


  ${({ displayOpacity }) => (displayOpacity && css`
  :hover {
    opacity: 1;
  }
  `)}
`;

const ButtonWrapper = styled('span', { shouldForwardProp: (prop) => prop !== 'hasVariants' })`
  display: flex;
  flex-direction: center;
  width: 90%;
  height: 100%;

  ${({ hasVariants }) => hasVariants && css`
    width: 70%;
  `}
`;

const SizeSelect = styled(Select, { shouldForwardProp: (prop) => prop !== 'showWarningMsg' })`
  color: ${(props) => props.theme.color.brand};
  outline: 0;
  width: 25%;
  height: 100%;
  font-size: ${(props) => props.theme.modularScale.fourteen};
  border: 0.1rem solid ${({ showWarningMsg, theme }) => (showWarningMsg ? theme.color.brandA11yRed : theme.color.borderBlue)};
  `;

const WarningText = styled(Typography)`
  font-family: ${(props) => props.theme.font.sans};
  color: ${(props) => props.theme.color.brandA11yRed};
  font-size: ${({ theme }) => theme.modularScale.sixteen};
  display: block;
  line-height: 1.5;
`;

const WarningWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.2rem 0;
`;

const CallToActionWrapper = styled.div`
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 0 2vh;
  `;

const FILTERED_STICKYMOBILE_EMO_PROPS = new Set(['isVisible', 'notAvailable', 'showWarningMsg']);

const StickyMobileProductAdd = styled('div', {
  shouldForwardProp: (prop) => !FILTERED_STICKYMOBILE_EMO_PROPS.has(prop)
})`
  position: fixed;
  left: 0;
  bottom: 0;
  height: ${({ showWarningMsg }) => (showWarningMsg ? '15%' : '10%')};
  width: 100%;
  display: ${({ notAvailable }) => (notAvailable ? 'none' : 'flex')};
  flex-direction: column;
  justify-content: ${(showWarningMsg) => (showWarningMsg ? 'space-evenly' : 'space-around')};
  background-color: white;
  box-shadow: 0px -2px 16px rgba(0,0,0,0.1);
  z-index: ${(props) => props.theme.layers.downstage - 1};

  @media (min-width: ${(props) => props.theme.breakpoint.medium}) {
    display: none;
  }

  @media (min-width: ${({ theme }) => theme.breakpoint.small}) and (max-width: ${({ theme }) => theme.breakpoint.medium}) {
    height: 12%;
  }

  ${({ isVisible, theme }) => (isVisible ? css`
    animation: ${theme.animations.stickyAppear} ${theme.animation.fast};
  ` : css`
    animation: ${theme.animations.stickyDisappear} ${theme.animation.fast} forwards;
  `
  )}
`;

const StickyProductAdd = (props) => {
  const {
    state: {
      warning,
      quantityChange,
      buttonText,
      stickyAddedToCart,
      onPageAddedToCart,
      requireMonogram: contextRequireMonogram,
      wishlistRef,
      monogramFormRef,
      onPageCTARef,
      monogramColor,
      monogramFont,
      monogramText
    },
    updateWarningMessage,
    onStickyAddedToCart,
    setMissingMonogramFields,
    resetMissingMonogramFields
  } = useProduct();

  const { state: { isOpened } } = useSearch();

  const getOptionType = () => props.product?.option_types?.[0]?.name || 'Size';
  const hasVariants = (() => {
    const variantsArray = props.product?.variants;
    if (!variantsArray || variantsArray.length === 1) return false;
    return variantsArray.every((v) => v.option_values.length > 0);
  })();

  const [addingToCart, setAddingToCart] = useState(false);
  const [requireMonogram, setRequireMonogram] = useState(contextRequireMonogram);
  const [monogramErrors, setMonogramErrors] = useState({});
  const [displayButtonText, setDisplayButtonText] = useState('Add to bag');
  const [optionOutOfStock, setOptionOutOfStock] = useState(false);
  const [option, setOption] = useState('0');
  const [optionType] = useState(getOptionType());
  const [outOfStock, setOutOfStock] = useState(false);
  const isVisible = useObserver(onPageCTARef, {
    root: null,
    rootMargin: '0px',
    threshold: 0.5
  });

  useEffect(() => {
    // to detect which fields are missing
    if (Object.keys(monogramErrors).length) {
      setMissingMonogramFields(monogramErrors);
    } else {
      resetMissingMonogramFields();
    }
  }, [monogramErrors]);

  const reachedStockLimit = (selectedVariant) => {
    if (selectedVariant && selectedVariant.total_on_hand > 0) {
      const itemQuantity = props.cart?.line_items?.
        find((x) => x.variant_id === selectedVariant?.id)?.quantity || 0;

      const priceObj = props?.vendor
        ? selectedVariant.prices.find((x) => x.vendor_id === props.vendor)
        : selectedVariant?.prices[0];

      const max = 10;

      const count = priceObj.total_on_hand - itemQuantity > max
        ? max : priceObj.total_on_hand - itemQuantity;

      return count <= 0;
    }

    return false;
  };

  const canSubmit = () => {
    if (props.cart.loading) return false;

    let isMonogramValid = true;
    const missingMonogramFields = {};

    if (props.variant?.prices?.length && requireMonogram) {
      const { monogram_customizations = {} } = props.variant.prices[0].monogram;

      let isColorValid = true;
      let isFontValid = true;

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'colors')) {
        isColorValid = monogram_customizations.colors.length === 0 || monogramColor;
      }

      if (Object.prototype.hasOwnProperty.call(monogram_customizations, 'fonts')) {
        isFontValid = monogram_customizations.fonts.length === 0 || monogramFont;
      }

      const isTextValid = monogramText;

      missingMonogramFields.missingMonogramColor = !isColorValid;
      missingMonogramFields.missingMonogramFont = !isFontValid;
      missingMonogramFields.missingMonogramText = !isTextValid;

      isMonogramValid = !!isColorValid && !!isFontValid && !!isTextValid;
    }

    if (!isMonogramValid && requireMonogram) {
      setMonogramErrors(missingMonogramFields);
      return 'Please provide all monogram options';
    }

    if (outOfStock || optionOutOfStock) return 'OOS';
    if (option === null) return `Please select a ${optionType.toLowerCase()}`;

    return true;
  };

  const handleOnSelectChange = (e) => {
    const { variants = [] } = props.product;

    setOption(e.currentTarget.value);

    if (e.currentTarget.value.includes('(SOLD OUT)')) {
      setOptionOutOfStock(true);
      setDisplayButtonText('Out Of Stock - Notify Me');
    } else {
      setOptionOutOfStock(false);
    }

    // update the variant with the user-selected variant
    let newVariant;
    if (e.currentTarget.value.includes('(')) {
      const trimmedValue = e.currentTarget.value.split('(')[0].trim();
      newVariant = variants.find((v) => v.option_values[0].name === trimmedValue);
    } else {
      newVariant = variants.find((v) => v.option_values[0].name === e.currentTarget.value);
    }

    if (reachedStockLimit(newVariant)) {
      updateWarningMessage('No more left');
    } else if (warning.includes('monogram') !== true) {
      updateWarningMessage('');
    }

    props.onVariantChange(newVariant);

    if (!e.currentTarget.value.includes('(SOLD OUT)')) {
      trackLineItemChange({
        product: props.product,
        quantity: quantityChange,
        variant: newVariant
      });
    }
  };

  const renderSizeOptions = () => {
    if (hasVariants) {
      const { variants = [] } = props.product;
      const options = [];

      variants.forEach((v) => {
        let oosText = '';

        if (v.total_on_hand === 1) oosText = '(Only 1 Left!)';
        if (v.total_on_hand === 0) oosText = '(SOLD OUT)';

        options.push(<option key={v.option_values[0].name.trim()}>{`${v.option_values[0].name} ${oosText}`}</option>);
      });

      return options;
    }

    return [];
  };

  const handleOnSubmit = (e) => {
    e.preventDefault();

    const validSubmission = canSubmit();

    if (validSubmission === true) {
      setAddingToCart(true);
      onStickyAddedToCart(true);
    } else if (validSubmission.includes('Please')) {
      updateWarningMessage(validSubmission);

      if (option && requireMonogram) {
        global.window.scroll({
          left: 0,
          top: monogramFormRef.getBoundingClientRect().top
            + global.window.scrollY
            - (global.window.innerHeight / 2.5),
          behavior: 'smooth'
        });
      }
    } else if (validSubmission.includes('OOS')) {
      global.window.scroll({
        left: 0,
        top: wishlistRef.getBoundingClientRect().top
          + global.window.scrollY
          - (global.window.innerHeight / 2),
        behavior: 'smooth'
      });
    }
  };

  // TODO: refactor method to avoid all these conditionals
  const buttonDisabled = () => (addingToCart
    || onPageAddedToCart
    || props.cart.loading
    || reachedStockLimit(props.variant)
    || warning === 'No more left');

  const isProductAvailable = () => props.product.available;
  const isProductDiscontinued = () => props.product.discontinued;

  useEffect(() => {
    const { variant: chosenVariant } = props;

    // to handle a selection from on-page CTA
    if (chosenVariant) {
      let oosText = '';

      if (chosenVariant.total_on_hand === 1) oosText = '(Only 1 Left!)';
      if (chosenVariant.total_on_hand === 0) oosText = '(SOLD OUT)';

      setOption(`${chosenVariant.option_values[0].name.trim()} ${oosText}`);
      setOptionOutOfStock(chosenVariant.total_on_hand === 0);
    } else {
      // to clear the option if item was added to cart via on page
      setOption(null);
    }
  }, [props.variant]);

  useEffect(() => {
    if (buttonText === displayButtonText) return;

    if (buttonText === 'Out of Stock') {
      setDisplayButtonText('Out of Stock - Notify Me');
      setOutOfStock(true);
    } else {
      // to handle if the user switches from an
      // OOS variant to a in-stock variant
      if (buttonText === 'Add to bag' && (!outOfStock || !optionOutOfStock)) {
        setOutOfStock(false);
        setOptionOutOfStock(false);
      }

      setDisplayButtonText(buttonText);
    }
  }, [buttonText]);

  useEffect(() => {
    setRequireMonogram(contextRequireMonogram);
  }, [contextRequireMonogram]);

  useEffect(() => {
    // if the product was successfully added to the cart
    // then all context values would be false,
    // so reset addingToCart to allow ATB button to be abled
    if (!onPageAddedToCart && !stickyAddedToCart && addingToCart) setAddingToCart(false);
  }, [onPageAddedToCart, stickyAddedToCart]);

  return (
    <StickyMobileProductAdd
      showWarningMsg={warning.length > 0 || reachedStockLimit(props.variant)}
      isVisible={isVisible && !isOpened}
      notAvailable={!isProductAvailable() || isProductDiscontinued()}
    >
      {warning.length || reachedStockLimit(props.variant) ? (
        <WarningWrapper>
          <WarningText element="p" like="label-1">
            {reachedStockLimit(props.variant) ? 'No more left' : warning}
          </WarningText>
        </WarningWrapper>
      ) : null}
      <CallToActionWrapper>

        {hasVariants && !outOfStock && !optionOutOfStock ? (
          <SizeSelect
            name="size"
            id="sticky-product-add-to-cart-size-select"
            value={!option ? '0' : option.trim()}
            onChange={handleOnSelectChange}
            outline
            showWarningMsg={(warning.length > 0 && !warning.includes('monogram')) || reachedStockLimit(props.variant)}
          >
            <option value="0" disabled hidden>{`${optionType}`}</option>
            {renderSizeOptions()}
          </SizeSelect>
        ) : null}
        <ButtonWrapper
          hasVariants={hasVariants && !outOfStock && !optionOutOfStock}
        >
          <SubmitButton
            type="submit"
            data-test-id="add_to_cart"
            id="sticky-product-add-to-cart-button"
            disabled={buttonDisabled()}
            onClick={handleOnSubmit}
            displayOpacity={!buttonDisabled()}
          >
            <>
              {displayButtonText}
            </>
          </SubmitButton>
        </ButtonWrapper>
      </CallToActionWrapper>
    </StickyMobileProductAdd>
  );
};

StickyProductAdd.defaultProps = {
  cart: {},
  variant: null,
  vendor: null
};

StickyProductAdd.propTypes = {
  cart: PropTypes.object,
  onVariantChange: PropTypes.func.isRequired,
  product: PropTypes.object.isRequired,
  variant: PropTypes.object,
  vendor: PropTypes.number
};

const mapStateToProps = (state) => ({
  cart: state.cart
});

export default connect(mapStateToProps, null)(StickyProductAdd);
